import {
  useState,
  useContext,
  useCallback,
  createContext,
  useEffect,
  useRef
} from 'react';

import {
  clientErrorTracker,
  trans,
  useSoyuzClientConfig,
  useSoyuzFlag,
  useSoyuzLocales
} from '@spotahome/soyuz/client';

import './RecaptchaContext.scss';
import { useThemeContext } from '..';

function getRecaptchaEnterprise() {
  const g = window?.grecaptcha;
  return g?.enterprise;
}

const getCaptchaFunction = fnName => getRecaptchaEnterprise()[fnName] || null;

const RECAPTCHA_NET_HOST = 'https://www.recaptcha.net';

const ReCaptchaContext = createContext(null);

const noOp = () => {};

export function ReCaptchaProvider({ children }) {
  const { recaptchaSiteKey, recaptchaSiteKeys } = useSoyuzClientConfig();
  const { current: locale } = useSoyuzLocales();
  const { theme } = useThemeContext();

  const loadRecaptcha = useCallback(() => {
    if (getRecaptchaEnterprise()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const qs = new URLSearchParams({ render: recaptchaSiteKey, hl: locale });
      const url = `${RECAPTCHA_NET_HOST}/recaptcha/enterprise.js?${qs.toString()}`;
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  const executeRecaptcha = useCallback(
    async action => {
      if (!recaptchaSiteKey) {
        throw new Error('Missing reCAPTCHA site key.');
      }

      const api = getRecaptchaEnterprise();

      if (!api?.execute) {
        throw new Error('reCAPTCHA Enterprise API not available.');
      }

      if (!action) {
        throw new Error(
          'Missing reCAPTCHA action. Pass an action to executeRecaptcha(action).'
        );
      }

      return new Promise((resolve, reject) => {
        api.ready(() => {
          api.execute(recaptchaSiteKey, { action }).then(resolve).catch(reject);
        });
      });
    },
    [recaptchaSiteKey]
  );

  const renderRecaptcha = useCallback(
    (wrapper, onChange, onExpired, onError) => {
      const render = getCaptchaFunction('render');
      return render(wrapper, {
        sitekey: recaptchaSiteKeys[theme] || recaptchaSiteKeys.spotahome,
        callback: onChange,
        theme: 'light',
        type: 'image',
        tabindex: 0,
        'expired-callback': onExpired,
        'error-callback': onError,
        size: 'normal',
        // stoken: this.props.stoken,
        hl: locale,
        badge: 'bottomright',
        isolated: false
      });
    },
    []
  );

  return (
    <ReCaptchaContext.Provider
      value={{ executeRecaptcha, loadRecaptcha, renderRecaptcha }}
    >
      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptcha(action) {
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(
    !TenantRecaptchaFlag.isOn()
  );
  const [hasRecaptchaError, setHasRecaptchaError] = useState(false);
  const { executeRecaptcha, loadRecaptcha, renderRecaptcha } =
    useContext(ReCaptchaContext);

  useEffect(() => {
    if (TenantRecaptchaFlag.isOn()) {
      loadRecaptcha()
        .then(() => setIsRecaptchaReady(true))
        .catch(() => setHasRecaptchaError(true));
    }
  }, [TenantRecaptchaFlag]);

  return {
    isRecaptchaReady,
    hasRecaptchaError,
    executeRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return null;
      }

      let recaptchaToken = null;

      try {
        recaptchaToken = await executeRecaptcha(action);
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: `ReCaptchaContext-useExecuteRecaptcha-${action}`
        });
      }

      return recaptchaToken;
    },
    renderRecaptcha
  };
}

const MAX_RETRIES = 5;

export function useInitRecaptcha({
  action,
  onChange = noOp,
  onExpired = noOp,
  onError = noOp,
  className
}) {
  const containerRef = useRef(null);
  const recaptchaIdRef = useRef(null);
  const executeRequestedRef = useRef(false);
  const retriesRef = useRef(0);

  const { renderRecaptcha, isRecaptchaReady, hasRecaptchaError } =
    useReCaptcha(action);

  const executeCaptcha = useCallback(() => {
    const execute = getRecaptchaEnterprise()?.execute || null;
    if (execute && recaptchaIdRef.current !== undefined) {
      return execute(recaptchaIdRef.current);
    }
    executeRequestedRef.current = true;
  }, []);

  useEffect(() => {
    let interval = null;
    const initializeCaptcha = () => {
      if (retriesRef.current >= MAX_RETRIES) {
        clearInterval(interval);
        return;
      }

      if (!recaptchaIdRef.current && containerRef.current) {
        const wrapper = document.createElement('div');
        recaptchaIdRef.current = renderRecaptcha(
          wrapper,
          onChange,
          onExpired,
          onError
        );
        containerRef.current.appendChild(wrapper);
        clearInterval(interval);
      }
      if (executeRequestedRef.current && recaptchaIdRef.current) {
        executeRequestedRef.current = false;
        executeCaptcha();
      }
      retriesRef.current += 1;
    };

    if (!recaptchaIdRef.current) {
      interval = setInterval(initializeCaptcha, 200);
    }

    return () => clearInterval(interval);
  }, []);

  const renderRecaptchaComponent = () => {
    if (hasRecaptchaError) {
      return <p className={className}>{trans('tenant.recaptcha.error')}</p>;
    }
    return <div ref={containerRef} className={className} />;
  };

  return {
    executeCaptcha,
    renderRecaptchaComponent,
    isRecaptchaReady
  };
}

export default ReCaptchaProvider;
